import { useState, useEffect } from 'react'
import { updateTimeOffRequest, getTimeOffRequests, supabase } from '../../lib/supabase'
import { Star, Phone, Mail, Check, X, MapPin, Award } from 'lucide-react'

const SPECIALTY_COLORS = {
  beginner: 'badge-teal', intermediate: 'badge-navy', advanced: 'badge-coral',
  kids: 'badge-gold', adaptive: 'badge-gray', private: 'badge-gray'
}

export default function AdminInstructors() {
  const [instructors, setInstructors] = useState([])
  const [selected, setSelected] = useState(null)
  const [pendingRequests, setPendingRequests] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      const res = await fetch('/api/admin/instructors', {
        headers: { Authorization: `Bearer ${session?.access_token}` },
      })
      const json = await res.json()
      const data = json.instructors || []
      setInstructors(data)
      if (data[0]) setSelected(data[0])
      setLoading(false)
    })
  }, [])

  useEffect(() => {
    if (!selected) return
    getTimeOffRequests(selected.id).then(({ data }) => {
      setPendingRequests((data || []).filter(r => r.status === 'pending'))
    })
  }, [selected])

  const handleTimeOff = async (id, status) => {
    await updateTimeOffRequest(id, status)
    setPendingRequests(r => r.filter(req => req.id !== id))
  }

  const initials = name => (name || '').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: '1.25rem', height: 'calc(100vh - 130px)', overflow: 'hidden' }}>
      {/* Instructor list */}
      <div className="card" style={{ overflow: 'auto' }}>
        <div className="card-header">
          <h3 style={{ fontWeight: 600, fontSize: '0.95rem' }}>All Instructors</h3>
          <span className="badge badge-teal">{instructors.length}</span>
        </div>
        {loading ? (
          <div style={{ padding: '2rem', textAlign: 'center' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
        ) : instructors.map(inst => (
          <div key={inst.id} onClick={() => setSelected(inst)}
            style={{
              padding: '0.875rem 1.25rem', cursor: 'pointer', transition: 'background 0.15s',
              background: selected?.id === inst.id ? 'rgba(14,170,164,0.06)' : 'transparent',
              borderLeft: selected?.id === inst.id ? '3px solid var(--teal)' : '3px solid transparent',
              display: 'flex', alignItems: 'center', gap: 10,
              borderBottom: '1px solid rgba(11,30,61,0.05)'
            }}>
            <div className="avatar" style={{ width: 38, height: 38, background: 'var(--ocean)', fontSize: '0.75rem' }}>
              {initials(inst.profile?.full_name)}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '0.875rem', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {inst.profile?.full_name}
              </div>
              <div style={{ fontSize: '0.72rem', color: 'var(--text-light)', display: 'flex', alignItems: 'center', gap: 4 }}>
                <Star size={10} fill="var(--gold)" color="var(--gold)" /> {inst.rating?.toFixed(1)}
                <span>· {inst.specialties?.slice(0, 2).join(', ')}</span>
              </div>
            </div>
            <div style={{
              width: 8, height: 8, borderRadius: '50%',
              background: inst.is_active ? 'var(--teal)' : 'var(--text-light)'
            }} />
          </div>
        ))}
      </div>

      {/* Detail panel */}
      {selected ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', overflow: 'auto' }}>
          {/* Profile card */}
          <div className="card">
            <div style={{ padding: '1.5rem', display: 'flex', gap: '1.25rem', alignItems: 'flex-start' }}>
              <div className="avatar" style={{ width: 64, height: 64, background: 'var(--ocean)', fontSize: '1.1rem' }}>
                {initials(selected.profile?.full_name)}
              </div>
              <div style={{ flex: 1 }}>
                <h2 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: 2 }}>{selected.profile?.full_name}</h2>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                  <Star size={14} fill="var(--gold)" color="var(--gold)" />
                  <span style={{ fontWeight: 600, fontSize: '0.875rem' }}>{selected.rating?.toFixed(1)}</span>
                  <span style={{ color: 'var(--text-light)', fontSize: '0.8rem' }}>({selected.total_reviews} reviews)</span>
                  <span className={`badge ${selected.is_active ? 'badge-teal' : 'badge-gray'}`}>
                    {selected.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
  <span style={{ fontSize: '0.9rem', color: 'var(--text-mid)', fontWeight: 500 }}>
    Surf Instructor
  </span>
</div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-ghost btn-sm">Edit Profile</button>
              </div>
            </div>
          </div>

          <div className="grid-2">
            {/* Specialties & certs */}
            <div className="card">
              <div className="card-header"><h3 style={{ fontWeight: 600, fontSize: '0.9rem' }}>Specialties</h3></div>
              <div className="card-body">
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: '1rem' }}>
                  {(selected.specialties || []).map(s => (
                    <span key={s} className={`badge ${SPECIALTY_COLORS[s] || 'badge-gray'}`} style={{ textTransform: 'capitalize' }}>{s}</span>
                  ))}
                  {(selected.specialties || []).length === 0 && <span style={{ fontSize: '0.8rem', color: 'var(--text-light)' }}>No specialties listed</span>}
                </div>
                <div className="form-label" style={{ marginBottom: 6 }}>Locations</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {(selected.locations || []).map(loc => (
                    <span key={loc} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.75rem', color: 'var(--text-mid)', background: 'rgba(11,30,61,0.05)', padding: '3px 10px', borderRadius: 50 }}>
                      <MapPin size={10} /> {loc}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Certifications */}
            <div className="card">
              <div className="card-header"><h3 style={{ fontWeight: 600, fontSize: '0.9rem' }}>Certifications</h3></div>
              <div className="card-body">
                {(selected.certifications || []).length === 0 ? (
                  <p style={{ fontSize: '0.8rem', color: 'var(--text-light)' }}>No certifications on file.</p>
                ) : (selected.certifications || []).map(c => (
                  <div key={c} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0.4rem 0', borderBottom: '1px solid rgba(11,30,61,0.05)' }}>
                    <Award size={14} color="var(--gold)" />
                    <span style={{ fontSize: '0.875rem' }}>{c}</span>
                    <span className="badge badge-green" style={{ marginLeft: 'auto' }}>Valid</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Time off requests */}
          {pendingRequests.length > 0 && (
            <div className="card">
              <div className="card-header">
                <h3 style={{ fontWeight: 600, fontSize: '0.9rem' }}>Pending Time Off Requests</h3>
                <span className="badge badge-gold">{pendingRequests.length}</span>
              </div>
              <div>
                {pendingRequests.map(req => (
                  <div key={req.id} style={{
                    display: 'flex', alignItems: 'center', gap: '1rem',
                    padding: '0.875rem 1.5rem', borderBottom: '1px solid rgba(11,30,61,0.05)'
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '0.875rem', fontWeight: 600 }}>
                        {req.start_date} – {req.end_date}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-light)' }}>
                        {req.reason || 'No reason provided'}
                      </div>
                    </div>
                    <button className="btn btn-sm" onClick={() => handleTimeOff(req.id, 'approved')}
                      style={{ background: 'rgba(14,170,164,0.1)', color: 'var(--teal)', border: '1px solid rgba(14,170,164,0.3)' }}>
                      <Check size={13} /> Approve
                    </button>
                    <button className="btn btn-sm" onClick={() => handleTimeOff(req.id, 'denied')}
                      style={{ background: 'rgba(232,97,74,0.1)', color: 'var(--coral)', border: '1px solid rgba(232,97,74,0.3)' }}>
                      <X size={13} /> Deny
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {selected.bio && (
            <div className="card">
              <div className="card-header"><h3 style={{ fontWeight: 600, fontSize: '0.9rem' }}>Bio</h3></div>
              <div className="card-body">
                <p style={{ fontSize: '0.875rem', color: 'var(--text-mid)', lineHeight: 1.7 }}>{selected.bio}</p>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-light)' }}>
          Select an instructor
        </div>
      )}
    </div>
  )
}
